/**
 * Class manages structure of columns taking into account colspan and rowspan.
 */
export class ColumnStructureService {
    static from(cells, columnCount) {
        return new ColumnStructureService(cells, columnCount);
    }
    static empty() {
        return new ColumnStructureService([], 0);
    }
    static columnNumber(index, colCount) {
        return index % colCount;
    }
    static rowNumber(index, colCount) {
        return Math.floor(index / colCount);
    }
    constructor(cells, columnCount) {
        this.cells = cells;
        this.columnCount = columnCount;
        this.structure = [];
        this.updateStructure();
    }
    updateStructure(cells = this.cells, columnCount = this.columnCount) {
        this.structure = [];
        this.columnCount = columnCount;
        this.cells = cells;
        if (columnCount !== undefined || columnCount !== 0) {
            this.cells.forEach((cell, index) => {
                const [row, column] = this.findFreeColumn(this.structure, index, columnCount);
                if (row !== undefined && column !== undefined)
                    this.fillColumn(cell, this.structure, index, column, row);
            });
        }
    }
    nthColumn(index, row) {
        return this.structure[row][index];
    }
    firstNColumns(count) {
        return this.structure.flatMap(row => [...new Set(row.slice(0, count))]);
    }
    leftmostColNumber(index) {
        for (const row of this.structure) {
            const column = row.indexOf(index);
            if (column !== -1) {
                return column;
            }
        }
        throw new Error('Could not find element index in the constructed table');
    }
    precedingLeftmostColNumber(index) {
        for (const row of this.structure) {
            const column = row.indexOf(index);
            if (column !== -1 && index > 0) {
                return this.leftmostColNumber(row[column - 1]);
            }
            else if (index === 0) {
                return -1;
            }
        }
        throw new Error('Could not find element index in the constructed table');
    }
    rightmostColNumber(index) {
        for (const row of this.structure) {
            const column = row.lastIndexOf(index);
            if (column !== -1) {
                return column;
            }
        }
        throw new Error('Could not find element index in the constructed table');
    }
    precedingRightmostColNumber(index) {
        for (const row of this.structure) {
            const column = row.indexOf(index);
            if (column !== -1 && index > 0) {
                return this.rightmostColNumber(row[column - 1]);
            }
            else if (index === 0) {
                return this.rightmostColNumber(row[column]);
            }
        }
        throw new Error('Could not find element index in the constructed table');
    }
    rowCount() {
        return this.structure.length;
    }
    findFreeColumn(table, index, colCount) {
        if (colCount === 0) {
            return [undefined, undefined];
        }
        const column = ColumnStructureService.columnNumber(index, colCount);
        const row = ColumnStructureService.rowNumber(index, colCount);
        if (table[row] === undefined) {
            return [row, 0];
        }
        else if (table[row][column] === undefined) {
            return [row, column];
        }
        else {
            return this.findFreeColumn(table, index + 1, colCount);
        }
    }
    fillColumn(elem, table, index, column, row) {
        if (table[row] === undefined) {
            table[row] = [];
        }
        table[row][column] = index;
        const rowEnd = elem.rowEnd();
        if (rowEnd !== undefined && rowEnd > row + 2) {
            this.fillColumn(elem, table, index, column, row + 1);
        }
        const colEnd = elem.colEnd();
        if (colEnd !== undefined && colEnd > column + 2) {
            this.fillColumn(elem, table, index, column + 1, row);
        }
    }
}
